import * as React from 'react';
import { OverviewReach, OverviewEquity, OverviewHeatmap, OverviewGender, OverviewSpark } from './OverviewCharts';
import { overviewPercent as pct, overviewGap, roundedGap, overviewClubSignal } from './OverviewFormatting';
import { reportingYear } from './OverviewCalendar';
export { overviewPeriodContext } from './OverviewCalendar';
const num = v => (v == null ? '—' : Number(v.toFixed(1)).toLocaleString('en-GB'));
const names = ['Not yet', 'Occasional', 'Regular', 'Committed'];
export function Card({
  title,
  subtitle,
  action,
  children,
  body = true,
  id
}: {
  title: string;
  subtitle: React.ReactNode;
  action?: React.ReactNode;
  children: React.ReactNode;
  body?: boolean;
  id?: string;
}) {
  return (
    <section className="slo-card" id={id} tabIndex={id ? -1 : undefined}>
      <header className="slo-cardHead">
        <div>
          <h2>{title}</h2>
          <p>{subtitle}</p>
        </div>
        {action && <div className="slo-cardAction">{action}</div>}
      </header>
      {body ? <div className="slo-cardBody">{children}</div> : children}
    </section>
  );
}
export function OverviewContent({
  data,
  comparison,
  periods,
  detailed,
  details,
  onToggle,
  onStudents,
  onDrawer,
  onNavigate,
  onDetailed,
  onExport,
  onPeriodChange,
  canStudents
}: {
  data: any;
  comparison: any;
  periods: any;
  detailed: boolean;
  details: string[];
  onToggle: (key: string) => void;
  onStudents: (filters?: any) => void;
  onDrawer: (value: any) => void;
  onNavigate: (path: string) => void;
  onDetailed: () => void;
  onExport: () => void;
  onPeriodChange?: () => void;
  canStudents: boolean;
}) {
  const reach = data.kpis.find(k => k.key === 'reach'),
    notyet = data.kpis.find(k => k.key === 'notyet'),
    coverage = data.kpis.find(k => k.key === 'coverage');
  const previous = comparison?.comparison,
    threshold = data.overviewSettings?.equityThreshold || 10;
  const year = reportingYear(periods, data);
  const yearName =
    year?.name?.replace(/(\d{4})\/(\d{2})(\d{2})/, '$1/$3') || new Date(data.period.from).getUTCFullYear().toString();
  const previousYear = year ? `${Number(year.start.slice(0, 4)) - 1}/${year.start.slice(2, 4)}` : 'Last year';
  const alerts = [...data.alerts.filter(a => a.state === 'open')].sort(
    (a, b) =>
      (({ high: 0, medium: 1, watch: 2, opportunity: 3 }[a.severity] ?? 4) -
        ({ high: 0, medium: 1, watch: 2, opportunity: 3 }[b.severity] ?? 4) || (b.affected || 0) - (a.affected || 0))
  );
  const show = (key: string) => detailed || details.includes(key);
  const more = (key: string, label: string) => (
    <button
      type="button"
      className={'slo-more ' + (details.includes(key) ? 'on' : '')}
      aria-expanded={details.includes(key)}
      onClick={() => onToggle(key)}
    >
      {label}
      <i />
    </button>
  );
  const fix = () => onNavigate('/slt/settings/thresholds');
  const delta = (key: string) => comparison?.deltas.find(d => d.key === key);
  const knownEquity = data.equity.filter(item => item.field !== 'gender');
  const sportEquity = data.sportEquity;
  const equity = [
    ...knownEquity,
    ...(sportEquity
      ? [{ ...sportEquity, field: 'sportGender', label: 'Girls · sport', filter: 'gender:FEMALE' }]
      : data.equity.filter(item => item.field === 'gender'))
  ];
  const gaps = equity.filter(item => item.gap != null).sort((a, b) => a.gap - b.gap),
    gap = gaps[0];
  const trendDelta = delta('reach');
  const summary = (
    <>
      {coverage?.value != null && coverage.value < 50 ? (
        <>
          <b>
            Attendance marks are recorded for {num(data.coverage.marked)} of {num(data.coverage.scheduled)} ended sessions
          </b>
          , so attendance figures below aren't reliable yet.{' '}
        </>
      ) : null}
      {reach?.value == null ? (
        <>Participation cannot yet be assessed for this selection. </>
      ) : trendDelta?.value != null ? (
        <>
          <b>Participation is {trendDelta.value >= 0 ? 'ahead of' : 'behind'} last year</b> ({pct(reach.value)} vs{' '}
          {pct(previous?.kpis.find(k => k.key === 'reach')?.value)} at the same point).{' '}
        </>
      ) : (
        <>
          <b>{pct(reach.value)} of students have taken part</b> in clubs or school sport during this period.{' '}
        </>
      )}
      {gap && gap.gap < 0 ? (
        <>
          <b>{gap.label} is the biggest gap</b>: reach is {pct(gap.group.rate)}, {Math.round(Math.abs(gap.gap))} pts
          behind peers.{' '}
        </>
      ) : null}
      {coverage?.value == null ? (
        <>Attendance recording coverage hasn't been measured yet.</>
      ) : coverage.value >= 50 ? (
        <>Attendance marks are recorded for {pct(coverage.value)} of ended sessions; attendance is reported separately from participation.</>
      ) : null}
    </>
  );
  const keys = detailed
    ? ['reach', 'regular', 'notyet', 'frequency', 'retention', 'coverage']
    : ['reach', 'regular', 'notyet', 'coverage'];
  const labels = {
    reach: 'Reach',
    regular: 'Regular participants',
    notyet: 'Not yet taken part',
    frequency: 'Sessions per participant',
    retention: 'Retention',
    coverage: 'Attendance recording coverage'
  };
  const groupRows = data.groups.filter(g => g.key !== 'unknown' && g.total !== 0),
    unknown = data.groups.find(g => g.key === 'unknown');
  const selectedClubs = data.clubs
    .map(c => ({ ...c, overviewSignal: overviewClubSignal(c) }))
    .filter(c => !!c.overviewSignal);
  const minGroup = data.suppression || 10;
  const traitRows = ['pupilsPremium', 'freeSchoolMeals', 'specialEducationNeeds', 'gender', 'boys']
    .map(field => {
      const item = data.equity.find(e => e.field === (field === 'boys' ? 'gender' : field));
      if (!item) return null;
      const group = field === 'boys' ? item.peers : item.group;
      const prior = previous?.equity.find(e => e.field === item.field);
      if (group.total < minGroup || group.rate == null) return null;
      return {
        key: field,
        label: field === 'boys' ? 'Boys' : field === 'freeSchoolMeals' ? 'FSM' : item.label,
        total: group.total,
        count: group.total - group.participating,
        filter: { characteristic: field === 'boys' ? 'gender:MALE' : item.filter },
        prior: field === 'boys' ? prior?.peers : prior?.group
      };
    })
    .filter(Boolean);
  const schoolNotYet = reach?.value == null ? null : 100 - reach.value;
  const yearCandidates = groupRows
    .filter(g => g.total >= minGroup && g.rate != null && schoolNotYet != null && 100 - g.rate > schoolNotYet)
    .sort((a, b) => a.rate - b.rate || Number(a.key) - Number(b.key));
  const notYetRows = [
    ...traitRows,
    ...yearCandidates.slice(0, 2).map(g => ({
      key: g.key,
      label: g.label,
      total: g.total,
      count: g.total - g.participating,
      filter: { years: g.key },
      prior: previous?.groups.find(p => p.key === g.key)
    }))
  ];
  const tierBar = (values: number[], large = false) => (
    <div className={'slo-tierBar ' + (large ? 'large' : '')}>
      {values.map((v, i) => (
        <i
          key={i}
          className={`tier-${i}`}
          style={{ width: `${(v * 100) / (values.reduce((a, b) => a + b, 0) || 1)}%` }}
        />
      ))}
    </div>
  );
  const tiersReady = !!data.tiers;
  const needsTeachingSetup = !data.availability.tiers;
  const teachingSetupReason = `Select teaching half-terms${year?.name ? ` for ${year.name}` : ' for this reporting period'}`;
  const hasYearSplit = groupRows.some(group => group.rate != null && group.regular != null);
  const tierWarning = (
    <p className="slo-help">
      {(needsTeachingSetup ? teachingSetupReason : data.tiersUnavailableReason) || (needsTeachingSetup
        ? teachingSetupReason
        : 'Frequency breakdown is unavailable for this selection')} ·{' '}
      <button type="button" className="sli-link" onClick={needsTeachingSetup ? fix : () => onDrawer({kind:'how'})}>
        {needsTeachingSetup ? 'Set up →' : 'How we count →'}
      </button>
    </p>
  );
  return (
    <div className="slo-content">
      <section className="slo-summary">
        <div className="slo-logoTile">
          <img src="/dist/images/logo-mark.png" alt="" />
        </div>
        <div>
          <div className="slo-summaryLabel">This week in one paragraph</div>
          <p>{summary}</p>
        </div>
        <button type="button" className="sli-button slo-small" onClick={() => onDrawer({ kind: 'squady' })}>
          Ask a follow-up
        </button>
      </section>
      <div className={'slo-kpis ' + (detailed ? 'detailed' : '')}>
        {keys.map(key => {
          const item = data.kpis.find(k => k.key === key) || { key, value: null };
          const d = delta(key);
          const shownDelta =
            d?.value == null ? null : d.unit === 'points' ? roundedGap(d.value) : Number(d.value.toFixed(1));
          const missing = item.value == null;
          const spark =
            key === 'reach'
              ? (data.trend || []).map(p => p.rate).filter(v => v != null)
              : (data.metricTrends?.[key] || []).filter(value => typeof value === 'number' && Number.isFinite(value));
          let sub =
            key === 'reach'
              ? `${num(item.count)} of ${num(item.total)} took part at least once`
              : key === 'regular'
              ? `${num(item.count)} students · ${data.overviewSettings?.tiers?.[1] || 3}+ sessions per half-term`
              : key === 'notyet'
              ? `${reach?.total ? pct((item.value * 100) / reach.total) : '—'} of roster`
              : key === 'coverage'
              ? `${num(item.count)} of ${num(item.total)} ended sessions have attendance marks`
              : key === 'frequency'
              ? `median ${num(item.median)}`
              : `${num(item.count)} of ${num(item.total)} previous participants`;
          const needsSetup = key === 'retention' ? item.action==='SETUP' : key === 'regular' && !data.availability.tiers;
          const needsPeriod = key === 'retention' && item.action==='PERIOD';
          const reason =
            key === 'regular'
              ? needsTeachingSetup ? teachingSetupReason : item.reason || 'Session dates missing for some participants'
              : key === 'retention'
              ? (item.reason || 'Needs comparable participation history') + (needsSetup && year?.name ? ` · Reporting year: ${year.name}` : '')
              : key === 'coverage'
              ? 'No completed sessions'
              : key === 'frequency'
              ? 'No dated sessions'
              : 'No participation records';
          const badge =
            !missing && key === 'coverage' && data.targets?.registerCoverage != null && item.value < data.targets.registerCoverage ? (
              <span className={'slo-delta ' + (item.value < 50 ? 'dn' : 'wr')}>Below target · {data.targets.registerCoverage}%</span>
            ) : !missing && key === 'notyet' && data.segments.ppnot != null ? (
              <span className="slo-delta wr">{num(data.segments.ppnot)} PP</span>
            ) : !missing && d?.value != null ? (
              <span
                className={
                  'slo-delta ' + (!shownDelta ? 'fl' : (key === 'notyet' ? -1 : 1) * d.value > 0 ? 'up' : 'dn')
                }
              >
                {!shownDelta
                  ? 'No change'
                  : d.unit === 'points'
                  ? overviewGap(d.value)
                  : `${shownDelta > 0 ? '+' : '−'}${num(Math.abs(shownDelta))}`}
              </span>
            ) : null;
          return (
            <button
              type="button"
              className={'slo-kpi sli-kpi ' + (missing ? 'unavailable' : '')}
              key={key}
              onClick={() =>
                missing
                  ? needsPeriod
                    ? onPeriodChange?.()
                    : needsSetup
                    ? fix()
                    : onDrawer({ kind: 'how' })
                  : key === 'coverage'
                  ? onNavigate('/slt/clubs')
                  : onStudents({ segment: item.segment || 'all', ...(key==='retention'?{measure:'participation'}:{}) })
              }
            >
              <span className="slo-kpiLabel">
                <span className="slo-kpiLabelText" title={labels[key]}>
                  {labels[key]}
                </span>
                {badge}
              </span>
              <span className="slo-kpiValue">
                <strong>{item.unit === 'percent' ? pct(item.value) : num(item.value)}</strong>
                {detailed && !missing && (
                  <OverviewSpark values={spark} down={spark.length > 1 && spark[spark.length - 1] < spark[0]} />
                )}
              </span>
              <small title={missing ? item.reason : key==='retention' ? item.detail : undefined}>
                {missing ? (
                  <>
                    {reason} · <span className="slo-fix">{needsPeriod ? 'Change period →' : needsSetup ? 'Set up →' : 'How we count →'}</span>
                  </>
                ) : (
                  sub
                )}
              </small>
              <span className="slo-chevron" aria-hidden="true">
                ›
              </span>
            </button>
          );
        })}
      </div>
      <div className="slo-grid reach-row">
        <Card
          title="Reach across the year"
          subtitle="Students who have taken part at least once, cumulative"
          action={
            <div className="slo-legend">
              <span>
                <i className="line current" />
                {yearName}
              </span>
              {previous && (
                <span>
                  <i className="line previous" />
                  {previousYear}
                </span>
              )}
            </div>
          }
        >
          <OverviewReach
            data={data}
            previous={comparison?.comparisonYearTrend || previous}
            matchedPrevious={previous?.kpis.find(k => k.key === 'reach')?.value}
            year={year}
            target={data.overviewSettings?.reachTarget}
            onSelect={() => onStudents()}
          />
        </Card>
        <Card
          id="slt-needs-attention"
          title="Needs attention"
          subtitle={`${alerts.length} open · ranked by impact`}
          action={alerts.length > 4 ? more('queue', `All ${alerts.length}`) : null}
          body={false}
        >
          <div className="slo-alertList">
            {alerts.slice(0, details.includes('queue') ? alerts.length : 4).map(alert => (
              <button
                type="button"
                className="slo-alert"
                key={alert.key}
                onClick={() => onDrawer({ kind: 'alert', value: alert })}
              >
                <span className="slo-alertTitle">
                  <span className={'slo-severity ' + alert.severity}>
                    {alert.severity === 'opportunity'
                      ? 'Opportunity'
                      : alert.severity === 'high'
                      ? 'High'
                      : alert.severity === 'medium'
                      ? 'Medium'
                      : 'Watch'}
                  </span>
                  <strong>{alert.title}</strong>
                  <span className="slo-alertDescription">
                    {(alert.body || alert.threshold || 'Review the evidence.').split(/\.\s/)[0].replace(/\.$/, '')}.
                  </span>
                </span>
                <span className="slo-chevron" aria-hidden="true">
                  ›
                </span>
              </button>
            ))}
            {!alerts.length && (
              <p className="slo-empty">Nothing open. Snoozed and reviewed alerts are in Reports › Review log.</p>
            )}
          </div>
        </Card>
      </div>
      <div className="slo-grid">
        <Card
          title="Who we're not reaching"
          subtitle={`Reach for each group vs everyone else · alert at ${threshold} pts`}
          action={!detailed ? more('heat', 'By year group') : null}
        >
          <OverviewEquity
            items={equity}
            population={data.population}
            threshold={threshold}
            onSelect={characteristic =>
              onStudents({ characteristic, area: characteristic === 'gender:FEMALE' && sportEquity ? 'sport' : 'all' })
            }
            onHow={() => onDrawer({ kind: 'how' })}
          />
          {show('heat') && (
            <>
              <div className="slo-subhead">
                By year group and characteristic<span>Click a cell to see the students · red outline = below 35%</span>
              </div>
              <OverviewHeatmap
                rows={data.heatmap}
                onSelect={(years, characteristic) => onStudents({ years, characteristic })}
              />
            </>
          )}
        </Card>
        <Card
          title="Year groups"
          subtitle={hasYearSplit ? 'Reach split into regular and occasional · tick = last year' : 'Students who have taken part · tick = last year'}
          action={!detailed ? more('tiers', 'How often') : null}
        >
          {groupRows.map(group => {
            const prior = previous?.groups.find(g => g.key === group.key),
              d = group.rate != null && prior?.rate != null ? group.rate - prior.rate : null,
              regular = group.regular != null && group.total > 0 ? (group.regular * 100) / group.total : 0;
            return (
              <button
                type="button"
                className="slo-year"
                key={group.key}
                onClick={() => (canStudents ? onStudents({ years: group.key }) : onDrawer({ kind: 'how' }))}
              >
                <b>{group.label}</b>
                <span className="slo-yearTrack">
                  <span className="slo-tierBar">
                    <i className="tier-3" style={{ width: `${regular}%` }} />
                    <i className="tier-2" style={{ width: `${Math.max(0, (group.rate || 0) - regular)}%` }} />
                  </span>
                  {prior?.rate != null && <i className="slo-yearTick" style={{ left: `${prior.rate}%` }} />}
                </span>
                <span className="slo-yearValue">
                  <b>{pct(group.rate)}</b> · {num(group.participating)}/{num(group.total)}{' '}
                  {d != null && (
                    <span className={'slo-delta mini ' + (!roundedGap(d) ? 'fl' : d > 0 ? 'up' : 'dn')}>
                      {!roundedGap(d) ? 'No change' : overviewGap(d)}
                    </span>
                  )}
                </span>
              </button>
            );
          })}
          <div className="slo-legend year-legend">
            {hasYearSplit && <span>
              <i className="tier-3" />
              Regular
            </span>}
            <span>
              <i className="tier-2" />
              {hasYearSplit ? 'Occasional' : 'Reach'}
            </span>
            {previous && (
              <span>
                <i className="tick" />
                Same point last year
              </span>
            )}
          </div>
          {unknown?.total > 0 && (
            <p className="slo-help">{num(unknown.total)} students have no year group in the MIS export.</p>
          )}
          {!tiersReady
            ? tierWarning
            : show('tiers') && (
                <>
                  <div className="slo-subhead">
                    How often students take part
                    <span>
                      Tiers per half-term ·{' '}
                      <button className="sli-link" onClick={fix}>
                        edit
                      </button>
                    </span>
                  </div>
                  {tierBar(data.tiers, true)}
                  <div className="slo-legend">
                    {data.tiers.map((v, i) => (
                      <button
                        type="button"
                        key={i}
                        onClick={() => onStudents({ segment: i === 0 ? 'notyet' : i === 1 ? 'occasional' : 'regular' })}
                      >
                        <i className={`tier-${i}`} />
                        {names[i]} {pct((v * 100) / data.tiers.reduce((a, b) => a + b, 0))}
                      </button>
                    ))}
                  </div>
                  <div className="slo-tierRows">
                    {(data.tierGroups || []).map(row => (
                      <button
                        type="button"
                        className="slo-tierRow"
                        key={row.filter}
                        onClick={() => onStudents({ characteristic: row.filter })}
                      >
                        <b>{row.label}</b>
                        {row.values ? (
                          tierBar(row.values)
                        ) : (
                          <span className="slo-suppressedText">Fewer than 10 students — hidden</span>
                        )}
                        <span>
                          {row.values
                            ? pct((row.values[0] * 100) / row.values.reduce((a, b) => a + b, 0)) + ' not yet'
                            : '—'}
                        </span>
                      </button>
                    ))}
                  </div>
                </>
              )}
        </Card>
      </div>
      {!detailed ? (
        <button type="button" className="slo-moreAll" onClick={onDetailed}>
          Show clubs, sport and the full breakdown
          <i />
        </button>
      ) : (
        <>
          <Card
            title="Clubs that need a decision"
            subtitle={`${selectedClubs.length} of ${data.clubs.length} clubs`}
            action={
              <button type="button" className="sli-link" onClick={() => onNavigate('/slt/clubs')}>
                All clubs
              </button>
            }
            body={false}
          >
            <div className="slo-tableWrap">
              <table className="slo-table">
                <thead>
                  <tr>
                    {['Club', 'Avg attendance', 'Capacity used', 'Last 6 weeks', 'Registers taken', 'Signal'].map(t => (
                      <th key={t}>{t}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {selectedClubs.map(club => (
                    <tr
                      key={club.id}
                      tabIndex={0}
                      role="button"
                      onClick={() => onDrawer({ kind: 'club', value: club })}
                      onKeyDown={event => {
                        if (event.key === 'Enter') onDrawer({ kind: 'club', value: club });
                      }}
                    >
                      <td>
                        <b>{club.name}</b>
                        <small>{[club.activity, club.scheduleLabel, club.leader].filter(Boolean).join(' · ')}</small>
                      </td>
                      <td>
                        {club.averageAttendance == null ? '—' : Math.round(club.averageAttendance)}{' '}
                        <span className="slo-muted">/ {num(club.maxParticipants)}</span>
                      </td>
                      <td>
                        <span className={club.capacityUsed > 100 ? 'slo-fill over' : 'slo-fill'}>
                          <i style={{ width: `${Math.min(100, club.capacityUsed || 0)}%` }} />
                        </span>
                        {pct(club.capacityUsed)}
                      </td>
                      <td>
                        <OverviewSpark
                          values={club.series.filter(p => p.present != null).map(p => p.present)}
                          down={
                            club.series.filter(p => p.present != null).slice(-1)[0]?.present <
                            club.series.find(p => p.present != null)?.present
                          }
                        />
                      </td>
                      <td>
                        <span
                          className={club.markCoverage != null && data.targets?.registerCoverage != null && club.markCoverage < data.targets.registerCoverage ? 'slo-registerWarning' : ''}
                        >
                          {pct(club.markCoverage)}
                        </span>
                      </td>
                      <td>
                        <span className={'slo-delta ' + club.overviewSignal.tone}>{club.overviewSignal.text}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {!selectedClubs.length && <p className="slo-empty">No clubs need a decision in this period.</p>}
            </div>
          </Card>
          <div className="slo-grid">
            <Card
              title="Sport by gender"
              subtitle="Share of girls and boys who played this term"
              action={
                <div className="slo-legend">
                  <span>
                    <i className="girls" />
                    Girls
                  </span>
                  <span>
                    <i className="boys" />
                    Boys
                  </span>
                </div>
              }
            >
              <OverviewGender
                rows={data.sportGender}
                onSelect={(years, characteristic) => onStudents({ area: 'sport', years, characteristic })}
              />
            </Card>
            <Card
              title="Who hasn't taken part yet"
              subtitle={`${num(notyet?.value)} students · share of each group`}
              action={
                <button type="button" className="sli-button" onClick={onExport}>
                  Generate report
                </button>
              }
              body={false}
            >
              <div className="slo-tableWrap">
                <table className="slo-table">
                  <thead>
                    <tr>
                      {['Group', 'Not yet', 'Share of group', 'vs last year'].map(t => (
                        <th key={t}>{t}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {notYetRows.map(row => {
                      const share = (row.count * 100) / row.total,
                        d = row.prior?.rate != null ? share - (100 - row.prior.rate) : null;
                      return (
                        <tr
                          key={row.key}
                          tabIndex={0}
                          role="button"
                          onClick={() => onStudents({ segment: 'notyet', ...row.filter })}
                          onKeyDown={event => {
                            if (event.key === 'Enter') onStudents({ segment: 'notyet', ...row.filter });
                          }}
                        >
                          <td>
                            <b>{row.label}</b>
                          </td>
                          <td>{num(row.count)}</td>
                          <td>
                            <span className="slo-fill red">
                              <i style={{ width: `${share}%` }} />
                            </span>
                            {pct(share)}
                          </td>
                          <td>
                            {d == null ? (
                              '—'
                            ) : (
                              <span className={'slo-delta ' + (!roundedGap(d) ? 'fl' : d < 0 ? 'up' : 'dn')}>
                                {!roundedGap(d) ? 'No change' : overviewGap(d)}
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {!notYetRows.length && <p className="slo-empty">No measurable groups in this selection.</p>}
                {yearCandidates.length > 2 && (
                  <button className="sli-link slo-allGroups" onClick={() => onStudents({ segment: 'notyet' })}>
                    All groups →
                  </button>
                )}
              </div>
            </Card>
          </div>
        </>
      )}
    </div>
  );
}
